import React, {createContext, useContext, useMemo} from 'react';

import {type UseHistoryKeysQueryResult} from '../../state/graphql/historyKeysQuery';
import {
  useKeyInfoQuery,
  useMultiRunsetKeyInfoQuery,
} from '../../state/runs/hooks';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import {type RunHistoryKeyInfo} from '../../types/run';
import {startFunctionPerformanceMeasure} from '../../util/datadog/workspace/functionPerformance';
import {dominantKeyType} from '../../util/runhelpers';

export type KeyInfoQueryResult = {
  loading: boolean;
  error: Error | null;
  refreshing: boolean;
  historyKeyInfo: RunHistoryKeyInfo & {sortedKeys: string[]};
};

type KeyInfoQueryContext = {
  keyInfoQuery: KeyInfoQueryResult;
  uniqueKeyTypes?: Set<string>;
};

function createKeyInfoQueryContext() {
  return createContext<KeyInfoQueryContext | undefined>(undefined);
}

function createKeyInfoQueryContextProvider(
  useKeyInfoQueryResult: (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => KeyInfoQueryResult,
  Context: React.Context<KeyInfoQueryContext | undefined>
) {
  return function KeyInfoQueryContextProvider({
    children,
    runSetRefs,
    customRunNamesRef,
  }: {
    children: React.ReactNode;
    runSetRefs: RunSetTypes.Ref[];
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined;
  }) {
    const keyInfoQueryResult = useKeyInfoQueryResult(
      runSetRefs,
      customRunNamesRef
    );
    const uniqueKeyTypes = useMemo(
      () => makeUniqueKeyTypes(keyInfoQueryResult),
      [keyInfoQueryResult]
    );

    const value = useMemo(
      () => ({
        keyInfoQuery: keyInfoQueryResult,
        uniqueKeyTypes,
      }),
      [keyInfoQueryResult, uniqueKeyTypes]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  };
}

const KeyInfoQueryContext = createKeyInfoQueryContext();

const KeyInfoQueryContextProviderComponent = createKeyInfoQueryContextProvider(
  (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => useKeyInfoQueryResult(runSetRefs, customRunNamesRef, useKeyInfoQuery),
  KeyInfoQueryContext
);

const MultiRunSetKeyInfoQueryContextProviderComponent =
  createKeyInfoQueryContextProvider(
    (
      runSetRefs: RunSetTypes.Ref[],
      customRunNamesRef: CustomRunNamesTypes.Ref | undefined
    ) =>
      useKeyInfoQueryResult(
        runSetRefs,
        customRunNamesRef,
        useMultiRunsetKeyInfoQuery
      ),
    KeyInfoQueryContext
  );

export const KeyInfoQueryContextProvider = ({
  children,
  runSetRefs,
  customRunNamesRef,
  allowMultiRunsetHistoryKeys = false,
}: {
  children: React.ReactNode;
  runSetRefs: RunSetTypes.Ref[];
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  allowMultiRunsetHistoryKeys?: boolean;
}) => {
  if (allowMultiRunsetHistoryKeys) {
    return (
      <MultiRunSetKeyInfoQueryContextProviderComponent
        runSetRefs={runSetRefs}
        customRunNamesRef={customRunNamesRef}>
        {children}
      </MultiRunSetKeyInfoQueryContextProviderComponent>
    );
  }
  return (
    <KeyInfoQueryContextProviderComponent
      runSetRefs={runSetRefs}
      customRunNamesRef={customRunNamesRef}>
      {children}
    </KeyInfoQueryContextProviderComponent>
  );
};

export const useKeyInfoQueryContext = () => {
  const context = useContext(KeyInfoQueryContext);

  if (!context) {
    throw new Error(
      'useKeyInfoQueryContext must be used within a KeyInfoQueryContextProvider'
    );
  }

  return context;
};

export const useMaybeKeyInfoQueryContext = () => {
  return useContext(KeyInfoQueryContext);
};

/**
 * Returns the set of distinct key types across all history keys, or undefined while the
 * query is loading/errored.
 *
 * Perf: the previous implementation went through RunHelpers.keyTypes() (which builds an
 * nKeys {key->type} map) then `new Set(Object.values(...))`, allocating two nKeys-sized
 * structures to produce a Set of at most ~20 types. Because the call site is
 * useMemo([keyInfoQuery]) the keyTypes single-slot memo misses on every recompute, so
 * that cost is paid in full each time. Computing the per-key winner straight into the
 * Set skips both allocations:
 *
 *   baseline -> this (local dev, darwin/node v22; cold = memo miss = realistic path):
 *     keys     cold time (median)        cold heap (net surviving alloc)
 *      5,000   0.9-1.7ms -> 0.2-0.4ms     1.2MB  -> 0.08MB
 *     25,000   9-13ms    -> 3-4ms         6.1MB  -> 0.4MB
 *    100,000   42-51ms   -> 12-18ms       16.7MB -> 1.6MB     (~3-4x time, ~10x heap)
 *
 * Per-key type selection delegates to RunHelpers.dominantKeyType — the same primitive
 * keyTypes uses — so the selection rule has a single source of truth, and this returns a
 * Set identical to `new Set(Object.values(keyTypes(keys)))`.
 */
function makeUniqueKeyTypes(
  keyInfoQuery: KeyInfoQueryResult
): Set<string> | undefined {
  if (keyInfoQuery.loading || keyInfoQuery.error != null) {
    return undefined;
  }

  const {historyKeyInfo} = keyInfoQuery;
  if (historyKeyInfo == null) {
    return undefined;
  }

  // Test exact release cohorts in fixed key-count buckets on query-result
  // updates. Duration * 1000 / keyCount gives microseconds per history key;
  // pair it with the autosync spans because the stacked change shifts the
  // dominant-key scan into memoized keyTypes rather than guaranteeing this
  // function alone becomes faster.
  const endMeasure = startFunctionPerformanceMeasure('makeUniqueKeyTypes');

  const uniqueTypes = new Set<string>();
  const {keys} = historyKeyInfo;
  for (const key in keys) {
    const winner = dominantKeyType(keys[key].typeCounts);
    if (winner !== undefined) {
      uniqueTypes.add(winner);
    }
  }
  endMeasure({keyCount: historyKeyInfo.sortedKeys.length});
  return uniqueTypes;
}

export const useKeyInfoQueryResult = (
  runSetRefs: RunSetTypes.Ref[],
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined,
  keyInfoQueryFunc: (
    runSetRefs: RunSetTypes.Ref[],
    customRunNamesRef: CustomRunNamesTypes.Ref | undefined
  ) => UseHistoryKeysQueryResult
): KeyInfoQueryResult => {
  const keyInfoQuery = keyInfoQueryFunc(runSetRefs, customRunNamesRef);
  const isKeyInfoLoading = keyInfoQuery.loading;
  const keyInfoQueryError = keyInfoQuery.error;
  const isKeyInfoRefreshing =
    !isKeyInfoLoading && keyInfoQueryError == null && keyInfoQuery.refreshing;

  const historyKeyInfo = useMemo(() => {
    if (isKeyInfoLoading || keyInfoQueryError != null) {
      return {sets: [], keys: {}, sortedKeys: []};
    }
    return keyInfoQuery.historyKeyInfo;
  }, [isKeyInfoLoading, keyInfoQuery, keyInfoQueryError]);

  return useMemo(() => {
    return {
      loading: isKeyInfoLoading,
      error: keyInfoQueryError,
      refreshing: isKeyInfoRefreshing,
      historyKeyInfo,
    };
  }, [
    historyKeyInfo,
    isKeyInfoLoading,
    keyInfoQueryError,
    isKeyInfoRefreshing,
  ]);
};
